import axios from "axios";
import { defineStore } from "pinia";

interface Notification {
  id: string;
  message: string;
  type: "info" | "success" | "warning" | "error";
}

// Base class for all notifications
class Notification implements Notification {
  id: string;
  message: string;
  type: Notification["type"];

  constructor(message: string, type: Notification["type"] = "info") {
    this.id = `notification-${self.crypto.randomUUID()}`;
    this.message = message;
    this.type = type;
  }
}

// Snack bar notification
class SnackBar extends Notification {
  delay: number;
  timer: NodeJS.Timeout | null;

  constructor(message: string, type?: Notification["type"], delay = 5000) {
    super(message, type);
    this.delay = delay;
    this.timer = null;
  }
}

export const useNotificationStore = defineStore("notification", () => {
  // State
  const snackBarList = ref<SnackBar[]>([]);

  // Getters
  const snackBars = computed(() => snackBarList.value);
  const hasSnacks = computed(() => snackBars.value.length > 0);

  // Actions
  const setSnackBarTimer = (snackBar: SnackBar) => {
    if (snackBar.timer != null) {
      clearTimeout(snackBar.timer);
      snackBar.timer = null;
    }

    snackBar.timer = setTimeout(() => {
      deleteSnackBar(snackBar.id);
    }, snackBar.delay);
  };

  const addSnackbar = (
    message: string,
    type?: Notification["type"],
    delay?: number,
  ) => {
    const snack = new SnackBar(message, type, delay);

    snackBarList.value.push(snack);
    setSnackBarTimer(snack);
  };

  const deleteSnackBar = (id: string) => {
    snackBarList.value = snackBarList.value.filter(
      (snackBar) => snackBar.id !== id,
    );
  };

  const clearSnackBars = () => {
    snackBarList.value = [];
  };

  const evalErrorMessage = (error: any, defaultMsg: string = "") => {
    const snackBarMsg = axios.isAxiosError(error)
      ? error.response?.data?.snackBarError
      : defaultMsg;

    if (snackBarMsg) addSnackbar(snackBarMsg, "error");
  };

  return {
    snackBars,
    hasSnacks,
    addSnackbar,
    clearSnackBars,
    evalErrorMessage,
    deleteSnackBar,
  };
});
